import styled, {createGlobalStyle} from 'styled-components';
import {getSpacing} from '@tickchak/react-share/app';
import {AI_BRAND_GRADIENT_BORDER_WIDTH, AI_BRAND_GRADIENT_HORIZONTAL, AI_BRAND_GRADIENT_VERTICAL} from './aiGeneratorButton/AIGeneratorButton.css';
import {alignEnd, dFlexColumn, dFlexColumnAlineEnd, justifyBetween} from '../../globalVariables/GlobalFlex';
import {mobileMedia, scrollbar} from '../../globalVariables/GlobalVariables';
import {bottom as innerPopBottom, close as innerPopClose, container as innerPopContainer, content as innerPopContent, title as innerPopTitle} from '../newPopup/innerPop/InnerPop.css';
import zIndex from '../../globalVariables/zIndex';

export const AI_PANEL_WIDTH = '389px';

export const panelGradientStyles = createGlobalStyle`
    .ai-message-generator-panel {
        border-color: transparent;
        border-inline-start-width: ${AI_BRAND_GRADIENT_BORDER_WIDTH};
        background-image:
            linear-gradient(var(--color-bg-base), var(--color-bg-base)),
            ${AI_BRAND_GRADIENT_VERTICAL};
        background-clip: padding-box, border-box;

        ${mobileMedia} {
            border-inline-start-width: 0;
            border-block-start-width: ${AI_BRAND_GRADIENT_BORDER_WIDTH};
            background-image:
                linear-gradient(var(--color-bg-base), var(--color-bg-base)),
                ${AI_BRAND_GRADIENT_HORIZONTAL};
        }
    }
    /*
     * SidePopup slides via the Tailwind 'translate' utilities, which set the
     * standalone CSS 'translate' property. The library's transition only lists
     * 'transform, opacity', so the slide snaps instead of animating. Re-declare
     * the transition to include 'translate' (keeps the library duration/easing).
     *
     * TODO(react-share): this is a missing capability, not a local preference —
     * SidePopup should list 'translate' in its own transition-property. Drop this
     * override once it does. See also aiAgentPanelStyles below.
     */
    @media (prefers-reduced-motion: no-preference) {
        .ai-message-generator-panel {
            transition-property: translate, transform, opacity !important;
        }
    }
    .ai-message-generator-header {
        border: none;
    }
`;

/**
 * Keep the task agent above full-screen forms (e.g. new event) and optionally hide its backdrop.
 *
 * TODO(react-share): the backdrop rules below select on `${dataCy}-backdrop`, an internal
 * naming detail of SidePopup, and will break silently when the library renames it. What is
 * actually needed are three props on SidePopup — `hideBackdrop`, `closeOnOutsideClick`, and
 * a z-index override. Replace this whole block with them once they exist.
 */
export const aiAgentPanelStyles = createGlobalStyle<{ $hideBackdrop?: boolean; $dataCy: string }>`
    .ai-agent-panel {
        z-index: ${zIndex.highestModal} !important;
    }
    ${p => p.$hideBackdrop ? `
        [data-cy="${p.$dataCy}-backdrop"] {
            display: none !important;
        }
    ` : `
        [data-cy="${p.$dataCy}-backdrop"] {
            z-index: ${zIndex.upperModal} !important;
            pointer-events: none !important;
            background: transparent !important;
            backdrop-filter: none !important;
        }
    `}
`;

export const panelInner = styled.div`
    height: calc(100% - 80px);
    padding: ${getSpacing('spaceXl')};
    ${dFlexColumn};
    ${justifyBetween};
`;

export const innerPopWrapper = styled.div`
    ${innerPopContainer} {
        border-color: transparent;
        border-style: solid;
        border-inline-start-width: ${AI_BRAND_GRADIENT_BORDER_WIDTH};
        background-color: var(--color-bg-base);
        background-image:
            linear-gradient(var(--color-bg-base), var(--color-bg-base)),
            ${AI_BRAND_GRADIENT_VERTICAL};
        background-clip: padding-box, border-box;
        border-radius: 0;
        border-start-start-radius: var(--border-radius-border-radius-2xl, 16px);
        border-end-start-radius: var(--border-radius-border-radius-2xl, 16px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

        width: 0;
        max-width: 100%;

        &.show {
            width: ${AI_PANEL_WIDTH} !important;
            max-width: 100%;
        }

        ${mobileMedia} {
            border-inline-start-width: 0;
            border-block-start-width: ${AI_BRAND_GRADIENT_BORDER_WIDTH};
            border-start-start-radius: var(--border-radius-border-radius-2xl, 16px);
            border-start-end-radius: var(--border-radius-border-radius-2xl, 16px);
            border-end-start-radius: 0;
            background-image:
                linear-gradient(var(--color-bg-base), var(--color-bg-base)),
                ${AI_BRAND_GRADIENT_HORIZONTAL};
        }
    }
    ${innerPopClose},
    ${innerPopTitle} {
        display: none;
    }
    ${innerPopBottom} {
        display: none;
        height: 0;
        border: none;
    }
    ${innerPopContent} {
        height: 100%;
        ${dFlexColumn};
        overflow: hidden;
    }
    .ai-message-generator-header {
        flex-shrink: 0;
        border: none;
    }
`;

export const innerPopPanelInner = styled.div`
    flex: 1;
    min-height: 0;
    padding: 0 ${getSpacing('spaceXl')} ${getSpacing('spaceXl')};
    ${dFlexColumn};
    ${justifyBetween};
    box-sizing: border-box;
`;

export const scrollArea = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    ${dFlexColumn};
`;

export const scrollColumn = styled.div`
    ${dFlexColumn};
    ${scrollbar};
    flex: 1;
    min-height: 0;
`;

export const chatColumn = styled.div`
    ${dFlexColumn}
    ${alignEnd}
    gap: ${getSpacing('spaceLg')};
    width: 100%;
`;

export const promptArea = styled.div`
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--border-radius-border-radius-2xl);
    padding: ${getSpacing('spaceSm')};
    box-shadow: 0 2px 6px -3px rgba(0, 0, 0, 0.28);
    ${dFlexColumnAlineEnd}
    gap: ${getSpacing('spaceLg')};
    height: max-content;
    flex-shrink: 0;
    margin-top: ${getSpacing('spaceSm')};
`;

export const PROMPT_TEXTAREA_MAX_HEIGHT_PX = 200;

export const promptTextarea = styled.textarea`
    width: 100%;
    border: none;
    resize: none;
    overflow: hidden;
    box-sizing: border-box;
    min-height: 20px;
    max-height: ${PROMPT_TEXTAREA_MAX_HEIGHT_PX}px;
    padding: ${getSpacing('spaceSm')} ${getSpacing('spaceLg')};
    font-size: var(--font-size-body-small, 14px);
    font-family: inherit;
    line-height: 1.2;
    color: var(--ta-color-text-default, #000);
    background: transparent;

    &::placeholder {
        color: var(--ta-color-text-subtle, rgba(0, 0, 0, 0.4));
    }

    &:focus {
        outline: none;
    }
    ${mobileMedia} {
        font-size: 16px;
    }
`;

export const errorText = styled.p`
    margin: ${getSpacing('spaceSm')} 0 0;
    font-size: var(--font-size-body-small, 14px);
    color: var(--color-state-danger-text);
`;
